import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from "react";
import { Rocket, Loader2, CheckCircle2, XCircle, CircleDashed, Download, Link as LinkIcon, Share2, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
const STATUS = {
  ready: { label: "Ready to ship", emoji: "🚀", color: "#10b981" },
  almost: { label: "Almost there", emoji: "⏳", color: "#f59e0b" },
  "not-yet": { label: "Not ready yet", emoji: "🔧", color: "#f43f5e" },
};

// Ship It Gate — one-click release readiness (Creation Depth + Visual QA + Playtest QA) → a
// shareable public release page. Exposes checkAndCelebrate() so the Compiler can auto-fire it when
// Vision Autopilot hits its target (confetti when a game turns "Ready").
const ShipIt = forwardRef(function ShipIt({ gameId, title, genre }, ref) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rel, setRel] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/compiler/${gameId}/ship-it`).then(({ data }) => { if (data.release) setRel(data.release); }).catch(() => {});
  }, [open, gameId]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/compiler/${gameId}/ship-it`, {}, { timeout: 45000 });
      setRel(data.release);
      const s = STATUS[data.release.status] || STATUS["not-yet"];
      toast.success(`Release check done — ${s.label} (${data.release.readiness}%).`);
      return data.release;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Release check failed — try again.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  const celebrate = () => {
    const fire = (ratio, opts) => confetti({ particleCount: Math.floor(220 * ratio), spread: 90, origin: { y: 0.6 }, colors: ["#10b981", "#38bdf8", "#a855f7", "#fbbf24"], ...opts });
    fire(0.3, { startVelocity: 60 }); fire(0.25, { spread: 120, decay: 0.92, scalar: 1.1 });
    setTimeout(() => fire(0.35, { spread: 130, startVelocity: 45 }), 220);
  };

  // Auto Ship Check: open the gate, run it, and celebrate if the game just turned "Ready".
  const checkAndCelebrate = useCallback(async () => {
    setOpen(true);
    const r = await run();
    if (r && r.status === "ready") { celebrate(); toast.success("🎉 Ready to ship! Your game passed the release gate."); }
  }, [run]);

  useImperativeHandle(ref, () => ({ checkAndCelebrate, open: () => setOpen(true) }), [checkAndCelebrate]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !rel) return;
    const ctx = canvas.getContext("2d");
    const W = 1200, H = 630;
    const st = STATUS[rel.status] || STATUS["not-yet"];
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#04070f"); grad.addColorStop(0.55, "#0a1830"); grad.addColorStop(1, st.color);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(4,7,15,0.6)"; ctx.fillRect(0, 0, W, H);

    ctx.textBaseline = "top"; ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText(`${st.emoji} GAMEVIEW · RELEASE CARD`, 60, 48);
    ctx.fillStyle = "rgba(255,255,255,0.97)"; ctx.font = "bold 60px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(rel.title || title || "My Game", 28), 58, 104);
    if (rel.genre || genre) { ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "27px Inter, system-ui, sans-serif"; ctx.fillText(truncate(rel.genre || genre, 46), 60, 182); }

    // Readiness ring
    const cx = 250, cy = 400, r = 120;
    ctx.lineWidth = 26; ctx.strokeStyle = "rgba(255,255,255,0.14)";
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = st.color; ctx.lineCap = "round";
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * (rel.readiness / 100))); ctx.stroke();
    ctx.textAlign = "center";
    ctx.fillStyle = st.color; ctx.font = "bold 84px Inter, system-ui, sans-serif"; ctx.fillText(`${rel.readiness}`, cx, cy - 60);
    ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.font = "24px Inter, system-ui, sans-serif"; ctx.fillText("release-ready", cx, cy + 44);

    // Status + stat lines
    ctx.textAlign = "left";
    ctx.fillStyle = st.color; ctx.font = "bold 46px Inter, system-ui, sans-serif"; ctx.fillText(`${st.emoji} ${st.label}`, 430, 300);
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "30px Inter, system-ui, sans-serif";
    const lines = [
      `Depth  ${rel.depth?.overall ?? "—"}%  ·  ${rel.depth?.state || ""}`,
      `Visuals  ${rel.visual != null ? rel.visual : "—"}`,
      `Playtest  ${rel.playtest != null ? rel.playtest : "—"}`,
    ];
    lines.forEach((l, i) => ctx.fillText(l, 432, 360 + i * 44));

    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "24px Inter, system-ui, sans-serif";
    ctx.fillText(`🔗 Release page — ${truncate(`${window.location.host}/release/${gameId}`, 44)}`, 60, 584);
  }, [rel, title, genre, gameId]);

  useEffect(() => { if (open && rel) draw(); }, [open, rel, draw]);

  const shareUrl = `${window.location.origin}/api/og/release/${gameId}`;

  // One-Tap Publish — when the game is Ready, ship it (public Arcade OR unlisted link) AND copy the
  // release link in a single tap. Auto-runs the release check first if it hasn't been run this session.
  const publishAndShare = useCallback(async (visibility = "public") => {
    let r = rel;
    if (!r || r.status !== "ready") r = await run();
    if (!r || r.status !== "ready") { toast.message("Not quite ready to ship — finish the checklist above first."); return; }
    setPublishing(true);
    try {
      const { data } = await api.post(`/games/${gameId}/publish`, { visibility }, { timeout: 45000 });
      try { await navigator.clipboard.writeText(shareUrl); } catch { /* clipboard blocked */ }
      if (data.status === "published") {
        setPublished(true);
        celebrate();
        toast.success(data.visibility === "unlisted"
          ? "🔗 Unlisted build published — private link copied!"
          : "🎉 Live in the Arcade — release link copied!");
      } else {
        toast.message(data.message || "Sent for a quick review before it goes live.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't publish — try again.");
    } finally {
      setPublishing(false);
    }
  }, [rel, run, gameId, shareUrl]);

  const download = async () => {
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "release-card.png"; a.click();
      URL.revokeObjectURL(url); toast.success("Release card downloaded!");
    }, "image/png");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Public release link copied!"); } catch { toast.error("Couldn't copy the link."); }
  };
  const nativeShare = async () => {
    const st = STATUS[rel?.status] || STATUS["not-yet"];
    try { await navigator.share({ title: "GameView", text: `"${rel?.title || title}" is ${st.label} (${rel?.readiness}% release-ready) — come play it!`, url: shareUrl }); } catch { /* cancelled */ }
  };

  const st = rel ? (STATUS[rel.status] || STATUS["not-yet"]) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7 px-3 text-xs" data-testid="compiler-shipit-btn">
          <Rocket className="w-3.5 h-3.5" /> Ship it
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="compiler-shipit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-[#10b981]" /> Ship It — Release Check</DialogTitle></DialogHeader>

        {!rel && (
          <div className="py-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))] leading-snug mb-3" data-testid="compiler-shipit-intro">
              One click runs the full release gate — Creation Depth + the Art Director's Visual QA + Playtester scores — and hands you a shareable release scorecard.
            </p>
            <Button onClick={run} disabled={loading} className="rounded-full w-full bg-gradient-to-r from-[#0ea5e9] to-[#10b981]" data-testid="compiler-shipit-run">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : <><Sparkles className="w-4 h-4" /> Run release check</>}
            </Button>
          </div>
        )}

        {rel && (
          <div className="space-y-3" data-testid="compiler-shipit-report">
            <div className="flex items-center gap-3">
              <div className="text-4xl font-extrabold tabular-nums" style={{ color: st.color }} data-testid="compiler-shipit-readiness">{rel.readiness}%</div>
              <div>
                <div className="text-base font-bold" style={{ color: st.color }} data-testid="compiler-shipit-status">{st.emoji} {st.label}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">overall release readiness</div>
              </div>
              <Button size="sm" variant="ghost" className="ml-auto h-7 px-2 text-xs gap-1" onClick={run} disabled={loading} data-testid="compiler-shipit-recheck">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Re-check"}
              </Button>
            </div>

            {rel.status === "ready" && (
              <div className="flex gap-2" data-testid="compiler-shipit-publish-row">
                <Button onClick={() => publishAndShare("public")} disabled={publishing} className="rounded-full flex-1 gap-2 font-bold bg-gradient-to-r from-[#10b981] to-[#0ea5e9] text-white" data-testid="compiler-shipit-publish">
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  {published ? "Published ✓ — copied" : "Publish to Arcade"}
                </Button>
                <Button variant="outline" onClick={() => publishAndShare("unlisted")} disabled={publishing} className="rounded-full gap-1.5" title="Publish as an unlisted link (not shown in the Arcade)" data-testid="compiler-shipit-publish-unlisted">
                  <LinkIcon className="w-4 h-4" /> Unlisted
                </Button>
              </div>
            )}

            <div className="space-y-1.5" data-testid="compiler-shipit-checklist">
              {rel.checklist.map((c) => (
                <div key={c.key} className="flex items-start gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2" data-testid={`compiler-shipit-check-${c.key}`}>
                  {c.pass ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                    : c.incomplete ? <CircleDashed className="w-4 h-4 mt-0.5 text-[hsl(var(--muted-foreground))] shrink-0" />
                      : <XCircle className="w-4 h-4 mt-0.5 text-rose-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium">{c.label}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="compiler-shipit-card" />
            <div className="flex flex-wrap gap-2">
              <Button onClick={download} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-[#0ea5e9] to-[#10b981]" data-testid="compiler-shipit-download"><Download className="w-4 h-4" /> Release card</Button>
              <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="compiler-shipit-copy"><LinkIcon className="w-4 h-4" /> Copy release link</Button>
              {typeof navigator !== "undefined" && navigator.share && (
                <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="compiler-shipit-share"><Share2 className="w-4 h-4" /> Share…</Button>
              )}
            </div>
            <a href={`/release/${gameId}`} target="_blank" rel="noreferrer" className="block text-center text-[11px] text-[hsl(var(--primary))] hover:underline" data-testid="compiler-shipit-viewpage">Open the public release page ↗</a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default ShipIt;
